import { useState } from 'react'
import { Search, Loader2, RefreshCw, CheckCircle, Eye, Clock, XCircle, AlertTriangle, FileQuestion, ArrowUpCircle, Info } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useLibrarianFindings, useLibrarianCrawl, useLibrarianResolve } from '@/hooks/useApi'
import { timeAgo } from '@/lib/utils'

const TYPE_CONFIG = {
  DISCOVERY: { label: 'Discovery', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: FileQuestion },
  MISALIGNMENT: { label: 'Misalignment', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: AlertTriangle },
  UPDATE_NEEDED: { label: 'Update Needed', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: ArrowUpCircle },
} as const

const STATUS_CONFIG: Record<string, { label: string; icon: typeof CheckCircle }> = {
  open: { label: 'Open', icon: Eye },
  pending_execution: { label: 'Pending Execution', icon: Clock },
  intentional: { label: 'Intentional', icon: CheckCircle },
  deferred: { label: 'Deferred', icon: Clock },
  dismissed: { label: 'Dismissed', icon: XCircle },
}

export function LibrarianPanel() {
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [selectedFinding, setSelectedFinding] = useState<string | null>(null)

  const { data, isLoading, refetch } = useLibrarianFindings('dashboard', statusFilter || undefined)
  const crawlMutation = useLibrarianCrawl()
  const resolveMutation = useLibrarianResolve()

  const findings = data?.findings ?? []
  const crawlHistory = data?.crawl_history ?? []
  const selected = selectedFinding ? findings.find(f => f.id === selectedFinding) : null

  const handleCrawl = () => {
    crawlMutation.mutate({ tenantId: 'dashboard', sources: ['cora-memory'] })
  }

  const handleResolve = (findingId: string, resolution: string) => {
    resolveMutation.mutate({ findingId, resolution, tenantId: 'dashboard' }, {
      onSuccess: () => { setSelectedFinding(null) },
    })
  }

  const statusCounts = findings.reduce<Record<string, number>>((acc, f) => {
    acc[f.status] = (acc[f.status] || 0) + 1
    return acc
  }, {})

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            Librarian Review
            {isLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            <div className="ml-auto flex items-center gap-2">
              {data && <Badge variant="outline">{data.count} findings</Badge>}
              <button
                onClick={handleCrawl}
                disabled={crawlMutation.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-accent/10 text-accent rounded-lg hover:bg-accent/20 transition-colors disabled:opacity-50"
              >
                {crawlMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Run Crawl
              </button>
              <button
                onClick={() => refetch()}
                className="p-1.5 text-text-muted hover:text-text-primary transition-colors rounded"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {crawlMutation.isSuccess && (
            <div className="mb-4 p-3 rounded-lg bg-success/10 border border-success/20 text-sm text-success">
              Crawl complete: {crawlMutation.data.findings_count} findings from {crawlMutation.data.memory_keys_indexed} memory keys
              {crawlMutation.data.errors.length > 0 && (
                <span className="text-amber-400 ml-2">({crawlMutation.data.errors.length} errors)</span>
              )}
            </div>
          )}

          {crawlMutation.isError && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
              Crawl failed: {crawlMutation.error instanceof Error ? crawlMutation.error.message : 'Unknown error'}
            </div>
          )}

          <div className="flex gap-2 mb-4 flex-wrap">
            <button
              onClick={() => setStatusFilter('')}
              className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${!statusFilter ? 'bg-accent/20 text-accent border-accent/30' : 'text-text-muted border-border hover:border-accent/30'}`}
            >
              All ({data?.count || 0})
            </button>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${statusFilter === key ? 'bg-accent/20 text-accent border-accent/30' : 'text-text-muted border-border hover:border-accent/30'}`}
              >
                {cfg.label} ({statusCounts[key] || 0})
              </button>
            ))}
          </div>

          {findings.length === 0 && !isLoading && (
            <div className="text-center py-8 text-text-muted text-sm">
              {statusFilter ? 'No findings with this status' : 'No findings yet. Run a crawl to scan knowledge sources.'}
            </div>
          )}

          <div className="space-y-2">
            {findings.map((finding) => {
              const typeConf = TYPE_CONFIG[finding.type] || TYPE_CONFIG.DISCOVERY
              const TypeIcon = typeConf.icon
              const statusConf = STATUS_CONFIG[finding.status] || STATUS_CONFIG.open
              const StatusIcon = statusConf.icon
              return (
                <div
                  key={finding.id}
                  onClick={() => setSelectedFinding(selectedFinding === finding.id ? null : finding.id)}
                  className="p-3 rounded-lg border border-border hover:border-accent/30 transition-colors cursor-pointer bg-surface-elevated"
                >
                  <div className="flex items-start gap-3">
                    <TypeIcon className="h-4 w-4 mt-0.5 shrink-0 text-text-muted" />
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`text-[10px] border ${typeConf.color}`}>{typeConf.label}</Badge>
                        <Badge variant="outline" className="text-[10px]">
                          <StatusIcon className="h-2.5 w-2.5 mr-1" />
                          {statusConf.label}
                        </Badge>
                        <span className="text-[10px] font-mono text-text-muted ml-auto">{finding.id}</span>
                      </div>
                      <p className="text-sm text-text-primary">{finding.question}</p>
                      <div className="flex items-center gap-3 text-[10px] text-text-muted">
                        <span>{finding.source}</span>
                        {finding.source_path && <span className="font-mono truncate max-w-[200px]">{finding.source_path}</span>}
                        {finding.memory_path && <span className="font-mono truncate max-w-[200px]">{finding.memory_path}</span>}
                        <span>{timeAgo(new Date(finding.created_at))}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {selected && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Info className="h-4 w-4" />
              {selected.id}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-text-muted">Type</span>
                <Badge className={`text-[10px] border ${(TYPE_CONFIG[selected.type] || TYPE_CONFIG.DISCOVERY).color}`}>
                  {(TYPE_CONFIG[selected.type] || TYPE_CONFIG.DISCOVERY).label}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Status</span>
                <span className="text-text-primary">{(STATUS_CONFIG[selected.status] || STATUS_CONFIG.open).label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Source</span>
                <span className="text-text-primary">{selected.source}</span>
              </div>
              {selected.source_path && (
                <div className="flex justify-between">
                  <span className="text-text-muted">Source Path</span>
                  <span className="text-text-primary font-mono text-xs max-w-[60%] text-right truncate">{selected.source_path}</span>
                </div>
              )}
              {selected.memory_path && (
                <div className="flex justify-between">
                  <span className="text-text-muted">Memory Path</span>
                  <span className="text-text-primary font-mono text-xs max-w-[60%] text-right truncate">{selected.memory_path}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-text-muted">Description</span>
                <span className="text-text-primary text-right max-w-[60%]">{selected.description}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Question</span>
                <span className="text-text-primary text-right max-w-[60%]">{selected.question}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Created</span>
                <span className="text-text-primary">{new Date(selected.created_at).toLocaleString()}</span>
              </div>
              {selected.resolved_at && (
                <div className="flex justify-between">
                  <span className="text-text-muted">Resolved</span>
                  <span className="text-text-primary">{new Date(selected.resolved_at).toLocaleString()}</span>
                </div>
              )}
            </div>

            {selected.status === 'pending_execution' && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-amber-400 flex items-center gap-2">
                <Clock className="h-4 w-4 shrink-0" />
                Approved -- will execute when CORA routing is available
              </div>
            )}

            {selected.status === 'open' && (
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleResolve(selected.id, 'approve')}
                  disabled={resolveMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-accent/10 text-accent rounded-lg hover:bg-accent/20 transition-colors disabled:opacity-50"
                >
                  <CheckCircle className="h-3 w-3" />
                  Approve
                </button>
                <button
                  onClick={() => handleResolve(selected.id, 'mark_intentional')}
                  disabled={resolveMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors disabled:opacity-50"
                >
                  <Eye className="h-3 w-3" />
                  Mark Intentional
                </button>
                <button
                  onClick={() => handleResolve(selected.id, 'defer')}
                  disabled={resolveMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-text-muted/10 text-text-muted rounded-lg hover:bg-text-muted/20 transition-colors disabled:opacity-50"
                >
                  <Clock className="h-3 w-3" />
                  Defer
                </button>
                <button
                  onClick={() => handleResolve(selected.id, 'dismiss')}
                  disabled={resolveMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50"
                >
                  <XCircle className="h-3 w-3" />
                  Dismiss
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {crawlHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Crawl History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {crawlHistory.map((crawl, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-surface-elevated text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-text-muted">{new Date(crawl.timestamp).toLocaleString()}</span>
                    <Badge variant="outline" className="text-[10px]">{crawl.sources.join(', ')}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-text-primary">{crawl.finding_count} findings</span>
                    {crawl.errors.length > 0 && (
                      <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30">{crawl.errors.length} errors</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
